/**
 * Props for `<sp-rating>` — a star rating, its numeric score, and an
 * optional link to the rating methodology. Stars are drawn for the score only: a rating of
 * 4.5 draws four full stars and a half star, with no empty stars after them unless `emptystars` is set.
 */
import { booleanAttribute } from "#ce/utils/props";

export const RatingLayout = Object.freeze({ INLINE: "inline", SUMMARY: "summary" });

const SUMMARY_BREAKPOINTS = Object.freeze(["sm", "md"]);

export const props = Object.freeze({
  /**
   * The score out of five. A missing or zero rating draws no stars and shows `no-rating`
   * in place of the score.
   *
   * @type {Number|String}
   * @default undefined
   * @storybook-control number
   */
  rating: {
    type: [Number, String],
    default: undefined,
  },
  /**
   * Digits shown after the decimal point of the score, 0 to 100: `1` prints a rating of 5 as `5.0`. Without it,
   * or outside that range, the score prints as a plain number.
   *
   * @type {Number|String}
   * @default undefined
   * @storybook-control number
   */
  fractionDigits: {
    type: [Number, String],
    default: undefined,
  },
  /**
   * The number of ratings the score is built from, shown under the stars when `showcount` is set.
   *
   * @type {Number|String}
   * @default undefined
   * @storybook-control number
   */
  count: {
    type: [Number, String],
    default: undefined,
  },
  /**
   * Draws an empty star for each position past the score, so every rated row has five stars.
   *
   * @type {Boolean|String}
   * @default false
   */
  emptystars: booleanAttribute(),
  /**
   * Accessible name of the stars, e.g. "Learn more about Dr. Smith's Patient Satisfaction Rating of 4.9 out of
   * 5.0.". Without it the stars read "Rating of 4.9 out of 5".
   *
   * @type {String}
   * @default undefined
   */
  label: {
    type: String,
    default: undefined,
  },
  /**
   * Arrangement of the parts. "inline" is one centred row: stars, score, info icon, info text, then the count on
   * a line of its own. "summary" puts the score in a first column spanning two rows, with the count above the stars
   * in a second column, from `summary-breakpoint` up; below it the parts wrap in that order, the count on a line of its
   * own.
   *
   * @type {String}
   * @default "inline"
   * @values inline, summary
   */
  layout: {
    type: String,
    default: "inline",
    validator: (value) => Object.values(RatingLayout).includes(value),
  },
  /**
   * Breakpoint where the "summary" layout moves from one column to two: "sm" is 751px, "md" is 992px.
   *
   * @type {String}
   * @default "sm"
   * @values sm, md
   * @storybook-control radio
   */
  summaryBreakpoint: {
    type: String,
    default: "sm",
    validator: (value) => SUMMARY_BREAKPOINTS.includes(value),
  },
  /**
   * Shows the number of ratings under the stars: "45 Ratings". Needs `count`.
   *
   * @type {Boolean|String}
   * @default false
   */
  showcount: booleanAttribute(),
  /**
   * Renders nothing when there is no rating, in place of the `no-rating` text.
   *
   * @type {Boolean|String}
   * @default false
   */
  hideunrated: booleanAttribute(),
  /**
   * Hides the numeric score, leaving the stars alone.
   *
   * @type {Boolean|String}
   * @default false
   */
  noScore: booleanAttribute(),
  /**
   * Destination for the stars and the score. Without it they render as plain text.
   *
   * @type {String}
   * @default undefined
   */
  url: {
    type: String,
    default: undefined,
  },
  /**
   * Destination for the trailing info icon, which renders only when this is set.
   *
   * @type {String}
   * @default undefined
   */
  infoUrl: {
    type: String,
    default: undefined,
  },
  /**
   * Text of a link to `info-url` after the info icon, e.g. "Learn about our survey". Renders only with `info-url`.
   *
   * @type {String}
   * @default undefined
   */
  infoText: {
    type: String,
    default: undefined,
  },
  /**
   * `sp-icon` glyph name of the info icon.
   *
   * @type {String}
   * @default "help-circle-filled"
   */
  infoIcon: {
    type: String,
    default: "help-circle-filled",
  },
  /**
   * `target` for the stars and the score, and for the info links when `info-target` is not set.
   *
   * @type {String}
   * @default undefined
   */
  target: {
    type: String,
    default: undefined,
  },
  /**
   * Additional relationship tokens on links that open new browsing contexts. `noopener` is always included.
   *
   * @type {String}
   * @default undefined
   */
  rel: {
    type: String,
    default: undefined,
  },
  /**
   * `target` for the info icon and the info text.
   *
   * @type {String}
   * @default undefined
   */
  infoTarget: {
    type: String,
    default: undefined,
  },
  /**
   * Shown in place of the score when there is no rating.
   *
   * @type {String}
   * @default undefined
   */
  noRating: {
    type: String,
    default: undefined,
  },
});
